import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Users, Search, MapPin, Filter, Building2, Phone, Mail, ArrowRight } from "lucide-react";

const STATUS_META = {
  new:            { label: "New",         color: "bg-slate-500/20 text-slate-300 border-slate-500/40" },
  contacted:      { label: "Contacted",   color: "bg-blue-500/20 text-blue-300 border-blue-500/40" },
  qualified:      { label: "Qualified",   color: "bg-cyan-500/20 text-cyan-300 border-cyan-500/40" },
  quote_sent:     { label: "Quote Sent",  color: "bg-purple-500/20 text-purple-300 border-purple-500/40" },
  won:            { label: "Won",         color: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" },
  lost:           { label: "Lost",        color: "bg-rose-500/20 text-rose-300 border-rose-500/40" },
};

const TYPE_META = {
  body_shop:  "Body Shop",
  glass_shop: "Glass Shop",
  dealership: "Dealership",
  insurance:  "Insurance",
  fleet:      "Fleet",
  other:      "Other",
};

export default function SalesProspectsList() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [city, setCity] = useState("");
  const [status, setStatus] = useState("");
  const [type, setType] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/sales/prospects");
      setRows(r.data || []);
    } catch (e) { /* noop */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  // Populate distinct cities from returned prospects
  const cities = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => { if (r.city && r.city.trim()) set.add(r.city.trim()); });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [rows]);

  // Client-side filter
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (city && (r.city || "").trim() !== city) return false;
      if (status && r.status !== status) return false;
      if (type && r.type !== type) return false;
      if (q) {
        const hay = `${r.company_name || ""} ${r.contact_name || ""} ${r.email || ""} ${r.phone || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, search, city, status, type]);

  const clearFilters = () => { setSearch(""); setCity(""); setStatus(""); setType(""); };
  const hasFilters = search || city || status || type;

  return (
    <div className="space-y-6 animate-slide-up" data-testid="prospects-list-page">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-indigo-600/15 border border-indigo-500/20 flex items-center justify-center">
            <Users className="w-6 h-6 text-indigo-400" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-bold mb-1">Sales</div>
            <h1 className="font-display text-3xl font-semibold tracking-tight">Prospects</h1>
            <p className="text-sm text-slate-400 mt-1">Full tabular list. Filter by city, status or type.</p>
          </div>
        </div>
        <Link to="/sales">
          <Button variant="outline" className="border-slate-700" data-testid="go-to-pipeline-btn">
            Pipeline view <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div className="md:col-span-2">
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 flex items-center gap-1.5 mb-1.5"><Search className="w-3 h-3" />Search</label>
            <Input data-testid="prospect-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Company, contact, email, phone" className="bg-[#030712] border-slate-800" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 flex items-center gap-1.5 mb-1.5"><MapPin className="w-3 h-3" />City</label>
            <select data-testid="prospect-city-filter" value={city} onChange={(e) => setCity(e.target.value)} className="w-full bg-[#030712] border border-slate-800 text-sm rounded-md h-9 px-3 text-slate-200">
              <option value="">All cities ({cities.length})</option>
              {cities.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 flex items-center gap-1.5 mb-1.5"><Filter className="w-3 h-3" />Status</label>
            <select data-testid="prospect-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className="w-full bg-[#030712] border border-slate-800 text-sm rounded-md h-9 px-3 text-slate-200">
              <option value="">All statuses</option>
              {Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 flex items-center gap-1.5 mb-1.5"><Building2 className="w-3 h-3" />Type</label>
            <select data-testid="prospect-type-filter" value={type} onChange={(e) => setType(e.target.value)} className="w-full bg-[#030712] border border-slate-800 text-sm rounded-md h-9 px-3 text-slate-200">
              <option value="">All types</option>
              {Object.entries(TYPE_META).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
        </div>
        {hasFilters && (
          <div className="mt-3 flex items-center justify-between text-xs text-slate-400">
            <span data-testid="prospect-count">Showing <span className="text-slate-100 font-mono">{filtered.length}</span> of <span className="text-slate-500 font-mono">{rows.length}</span></span>
            <button data-testid="clear-filters-btn" onClick={clearFilters} className="text-blue-400 hover:text-blue-300 underline underline-offset-2">Clear filters</button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="rounded-xl border border-slate-800 bg-[#0B1120] overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-slate-900/50 text-[10px] uppercase tracking-[0.14em] font-bold text-slate-500">
              <th className="text-left px-4 py-3">Company</th>
              <th className="text-left px-4 py-3">Contact</th>
              <th className="text-left px-4 py-3">City</th>
              <th className="text-left px-4 py-3">Type</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Phone</th>
              <th className="text-left px-4 py-3">Email</th>
              <th className="w-10"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className="text-center text-slate-500 py-10">Loading...</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={8} className="text-center text-slate-500 py-10" data-testid="no-prospects">No prospects match the current filters.</td></tr>
            )}
            {filtered.map((p) => {
              const s = STATUS_META[p.status] || { label: p.status, color: "bg-slate-500/20 text-slate-300 border-slate-500/40" };
              return (
                <tr key={p.id} data-testid={`prospect-row-${p.id}`} className="border-t border-slate-800/60 hover:bg-slate-900/30 transition">
                  <td className="px-4 py-2.5">
                    <div className="text-sm font-medium text-slate-100">{p.company_name || "-"}</div>
                  </td>
                  <td className="px-4 py-2.5 text-sm text-slate-300">{p.contact_name || "-"}</td>
                  <td className="px-4 py-2.5 text-sm text-slate-300">
                    {p.city ? <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3 text-slate-500" />{p.city}</span> : <span className="text-slate-600">-</span>}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-slate-400">{TYPE_META[p.type] || p.type || "-"}</td>
                  <td className="px-4 py-2.5">
                    <span className={`inline-block px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-bold border ${s.color}`}>{s.label}</span>
                  </td>
                  <td className="px-4 py-2.5 text-xs text-slate-400 font-mono">
                    {p.phone ? <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{p.phone}</span> : <span className="text-slate-600">-</span>}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-slate-400 truncate max-w-[200px]">
                    {p.email ? <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" />{p.email}</span> : <span className="text-slate-600">-</span>}
                  </td>
                  <td className="px-2 py-2.5">
                    <Link to={`/sales/prospects/${p.id}`} data-testid={`prospect-open-${p.id}`} className="text-slate-500 hover:text-blue-400">
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
